import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import LeadForm from "@/components/LeadForm";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Phone, Envelope, MapPin, Clock } from "@phosphor-icons/react";

export function Contact() {
  return (
    <div data-testid="contact-page">
      <SEO title="Contact Point Penta Dubai" description="Speak to a Point Penta advisor — 902, Ithra Tower, Al Garhoud, Dubai." path="/contact" />
      <section className="pt-6 pb-8 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]} />
          <div className="overline text-[#001588] mb-4">Contact</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-tight">A conversation<br /><span className="italic text-neutral-600">is a good place to start.</span></h1>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5 space-y-8">
            <Line icon={MapPin} title="Head office" body={<>902, Ithra Tower<br />Al Garhoud, Dubai, UAE</>} />
            <Line icon={Phone} title="Direct" body="+971 55 739 6664" />
            <Line icon={Envelope} title="Email" body="info@pointpenta.com" />
            <Line icon={Clock} title="Office hours" body={<>Monday – Saturday<br />9:00 a.m. – 7:00 p.m. GST</>} />
            <div className="border-t border-neutral-200 pt-8">
              <div className="overline text-[#001588] mb-2">Office hours</div>
              <p className="text-neutral-600">Sun – Thu · 9:00 – 19:00<br />Saturday viewings by appointment.</p>
            </div>
            <WhatsAppButton className="w-full sm:w-auto" testId="contact-whatsapp" />
          </div>
          <div className="md:col-span-7 border border-neutral-200 p-8 bg-[#ffffff]">
            <div className="overline text-[#001588] mb-4">Enquire</div>
            <h2 className="font-serif text-3xl mb-6">Tell us what you're looking for.</h2>
            <LeadForm source="contact" interest="general" testIdPrefix="contact" />
          </div>
        </div>
      </section>
    </div>
  );
}

function Line({ icon: Icon, title, body }) {
  return (
    <div className="flex gap-4">
      <div className="w-11 h-11 border border-[#001588] flex items-center justify-center flex-shrink-0"><Icon size={18} className="text-[#001588]" /></div>
      <div><div className="overline text-neutral-500 mb-1">{title}</div><div className="text-neutral-800">{body}</div></div>
    </div>
  );
}

export function Valuation() {
  return (
    <div data-testid="valuation-page">
      <SEO title="Free property valuation Dubai" description="48-hour comparable-sale analysis of your Dubai property, benchmarked to DLD transactions. No obligation." path="/valuation" />
      <section className="pt-6 pb-8 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Valuation", path: "/valuation" }]} />
          <div className="overline text-[#001588] mb-4">Complimentary valuation</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-tight">Know the number<br /><span className="italic text-neutral-600">before you make a move.</span></h1>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <p className="text-neutral-600 leading-relaxed mb-8 text-lg">
            We run a full comparable-sale analysis on your property, benchmarked to the latest Dubai Land Department transactions. Turnaround is 48 hours. There is no obligation to list.
          </p>
          <div className="border border-neutral-200 p-8 bg-[#ffffff]">
            <LeadForm source="valuation" interest="valuation" messagePlaceholder="Community, tower / villa type, size, current occupancy…" testIdPrefix="valuation" />
          </div>
        </div>
      </section>
    </div>
  );
}

export function ListWithUs() {
  return (
    <div data-testid="list-with-us-page">
      <SEO title="List your property with Point Penta" description="Bespoke marketing, discreet placement, partner-level negotiation. Sell your Dubai property with Point Penta." path="/list-with-us" />
      <section className="pt-6 pb-8 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "List with us", path: "/list-with-us" }]} />
          <div className="overline text-[#001588] mb-4">List with Point Penta</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-tight">Bespoke marketing.<br /><span className="italic text-neutral-600">Discreet placement.</span></h1>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-serif text-3xl mb-4">Why sell with us</h2>
            <ul className="space-y-3 text-neutral-600">
              {["Editorial-standard photography and videography", "Priority placement across our private network", "Buyer vetting — no time-wasters", "Direct partner-level negotiation", "Full DLD & Ejari administration"].map((l) => (
                <li key={l} className="border-b border-neutral-200 pb-3">· {l}</li>
              ))}
            </ul>
          </div>
          <div className="border border-neutral-200 p-8 bg-[#ffffff]">
            <div className="overline text-[#001588] mb-4">Submit your property</div>
            <LeadForm source="list-with-us" interest="sell" messagePlaceholder="Property address, community, size, asking price…" testIdPrefix="list-with-us" />
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
